import type { HTMLAttributes } from 'react';
import { forwardRef, useMemo } from 'react';

import Icon from '@/components/Icon/Icon';
import type { IconName } from '@/components/Icon/iconMap';
import HStack from '@/components/Layout/HStack';
import { cn } from '@/lib/cn';
import { useTranslation } from '@/lib/i18n';

type Color = 'positive' | 'negative' | 'neutral';
type Trend = 'up' | 'stay' | 'down';
type Variant = 'number' | 'percent';

const numberIcons: Record<Trend, IconName> = {
  up: 'MoveUpRightIcon',
  stay: 'MoveRightIcon',
  down: 'MoveDownRightIcon',
};

const percentIcons: Record<Trend, IconName> = {
  up: 'TrendingUpIcon',
  stay: 'MoveRightIcon',
  down: 'TrendingDownIcon',
};

type Props = {
  value: number;
  variant: Variant;
  color?: Color | 'auto';
  /**
   * trueの場合、効果を反転させます（up=negative/red, down=positive/green）
   * アラート数など、増加がネガティブな指標に使用します
   */
  invertEffect?: boolean;
};

export default forwardRef<
  HTMLDivElement,
  Props & HTMLAttributes<HTMLDivElement>
>(function TrendBadge(
  { value, color = 'auto', variant, invertEffect = false, className, ...rest },
  ref,
) {
  const { i18n } = useTranslation();
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language, {
        style: variant === 'number' ? 'decimal' : 'percent',
        maximumFractionDigits: 0,
      }),
    [i18n.language, variant],
  );

  const text = formatter.format(Math.abs(value));
  const trend = value > 0 ? 'up' : value < 0 ? 'down' : 'stay';
  const iconName =
    variant === 'number' ? numberIcons[trend] : percentIcons[trend];

  const getAutoColor = (): 'success' | 'error' | 'neutral' => {
    if (trend === 'stay') return 'neutral';
    if (invertEffect) {
      // 反転: up=negative(red), down=positive(green)
      return trend === 'up' ? 'error' : 'success';
    }
    // 通常: up=positive(green), down=negative(red)
    return trend === 'up' ? 'success' : 'error';
  };

  const colorVariant =
    color === 'auto'
      ? getAutoColor()
      : color === 'positive'
        ? 'success'
        : color === 'negative'
          ? 'error'
          : 'neutral';

  return (
    <HStack ref={ref} className={cn('items-center gap-1', className)} {...rest}>
      <Icon
        iconName={iconName}
        size={12}
        strokeWidth={2.25}
        colorVariant={colorVariant}
      />
      <span
        className={cn('text-labelSmall', {
          'text-success-on-neutral-fill': colorVariant === 'success',
          'text-error-on-neutral-fill': colorVariant === 'error',
          'text-secondary-on-neutral-fill': colorVariant === 'neutral',
        })}
      >
        {text}
      </span>
    </HStack>
  );
});
